import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';
import { ModalRatingAndNumReviews } from '../ModalRatingAndNumReviews';

export const CleanserAndRichMoisturizerModalContent = ({
  modalCard,
}: {
  modalCard: Card;
}) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modal.title}
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <ModalRatingAndNumReviews modalCard={modalCard} />
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Skincare is simple with the essential 3-step Curology routine: gentle
        cleanser, your personalized formula, and cream moisturizer for
        normal-to-dry skin. These dermatologist-designed essentials deliver
        clean, hydrated skin—without the worry of clogged pores.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>The cleanser:</strong> This is as gentle as it gets. Our
            lightly foaming face cleanser washes away dirt and excess
            oil—without stripping or clogging.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>The rich moisturizer:</strong> Our creamy, skin-replenishing
            moisturizer provides deep hydration for normal-to-dry skin with six
            moisturizing super-ingredients and zero pore-clogging gunk.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Use morning and night as go-to essentials in your daily skincare
        routine.
      </Typography>
      <ul className="cur-list-disc cur-ml-4">
        <li className="cur-mt-4">
          <Typography>
            <strong>The cleanser:</strong> Massage the cleanser onto your damp
            skin. Rinse off thoroughly with warm water, then pat your skin dry.
          </Typography>
        </li>
        <li className="cur-mt-4">
          <Typography>
            <strong>The rich moisturizer:</strong> Apply the rich moisturizer
            after washing your face and patting your skin dry.
          </Typography>
        </li>
      </ul>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <Typography className="cur-mt-4" weight="bold">
        The cleanser
      </Typography>
      <IngredientsAccordion
        body="Locks water in the skin's outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <Typography className="cur-mt-4" weight="bold">
        The rich moisturizer
      </Typography>
      <IngredientsAccordion
        body="Moisturizes, soothes, and protects skin while supporting healing"
        title="Allantoin"
      />
      <IngredientsAccordion body="Hydrates and soothes skin" title="Aloe" />
      <IngredientsAccordion
        body="Locks water in the skin's outer layer to maximize moisture levels"
        title="Glycerin"
      />
      <IngredientsAccordion
        body="Attracts and retains moisture for visibly plumper skin"
        title="Hyaluronic acid"
      />
      <IngredientsAccordion
        body="Softens skin and helps seal in moisture"
        title="Shea butter"
      />
      <IngredientsAccordion
        body="Helps lock in moisture and leaves skin feeling soft and supple"
        title="Squalane"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        <strong>The cleanser:</strong> Water/Aqua/Eau, Acrylates Copolymer,
        Cocamidopropyl Hydroxysultaine, Sodium Cocoyl Isethionate,
        Xylitylglucoside, Propanediol, Coco-glucoside, Anhydroxylitol, Stearic
        Acid, 1,2-Hexanediol, Xylitol, Panthenol, Coconut Alcohol, Citric Acid,
        PEG-150, Pentaerythrityl Tetrastearate, Stearyl Alcohol, Glycerin,
        Sodium Hydroxide, Sodium Chloride, Caprylhydroxamic Acid, Disodium EDTA,
        Xanthan Gum, Potassium Sorbate, Avena Sativa (Oat) Leaf Extract
      </Typography>
      <Typography className="cur-mt-4">
        <strong>The rich moisturizer:</strong> Water/Aqua/Eau, Caprylic/Capric
        Triglyceride, Cetearyl Alcohol, Cetyl Alcohol, Aloe Barbadensis Leaf
        Juice, Squalane, Glycerin, Dimethicone, Hydroxyethyl Acrylate/Sodium
        Acryloyldimethyl Taurate Copolymer, Limnanthes Alba (Meadowfoam) Seed
        Oil, Sodium Hyaluronate, Butyrospermum Parkii (Shea) Butter, Cetearyl
        Olivate, Sorbitan Olivate, Argania Spinosa Kernel Oil, Sodium PCA,
        Allantoin, Isohexadecane, Cetearyl Glucoside, Polysorbate 60, Sorbitan
        Isostearate, Phenoxyethanol, Ethylhexylglycerin, Citric Acid, Sodium
        Hydroxide
      </Typography>
    </>
  );
};
